import { type FallbackProps } from "react-error-boundary"
import { AlertTriangle, RotateCcw, Home, RefreshCw } from "lucide-react"

/**
 * Global error-boundary fallback. Shown when any child throws (including
 * during render). Gives the user two escape hatches: retry (resets the
 * boundary) and go home (hard nav to /dashboard).
 *
 * Special case — stale-chunk recovery:
 *   If the caught error is a "Failed to fetch dynamically imported
 *   module" / ChunkLoadError, it means a deploy has landed while the
 *   user's tab was open. lazy-with-retry.ts already tries one silent
 *   reload; if we still hit the boundary, we surface a distinct
 *   "New version available — reload now" prompt instead of the
 *   generic "Something went wrong" screen, and the primary button
 *   does a hard reload instead of just resetting the boundary.
 *
 * The `error.stack` is only shown in dev — production users get a clean
 * card. `console.error` on mount keeps the info in Sentry / Log Analytics
 * wherever the app is shipped.
 */
function isChunkLoadError(err: unknown): boolean {
  if (!err) return false
  const msg = err instanceof Error ? err.message : String(err)
  return (
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /Loading chunk \d+ failed/i.test(msg) ||
    /Loading CSS chunk \d+ failed/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /NetworkError when attempting to fetch resource/i.test(msg)
  )
}

export function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const isDev = import.meta.env.DEV
  const isChunkError = isChunkLoadError(error)

  // Ship the stack to whatever error sink is wired up.
  console.error("[ErrorBoundary caught]", error)

  if (isChunkError) {
    return (
      <div className="min-h-[400px] flex items-center justify-center p-8">
        <div className="max-w-lg w-full rounded-lg border border-primary/40 bg-card p-6 shadow-sm">
          <div className="flex items-start gap-3">
            <RefreshCw className="h-5 w-5 text-primary shrink-0 mt-0.5" aria-hidden />
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-semibold">A new version is available</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                We rolled out an update while this tab was open. Reload to
                pick up the fresh version — your work stays intact.
              </p>
              <div className="mt-5 flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    try {
                      sessionStorage.removeItem("edemics:chunk-reload-attempted")
                    } catch { /* ignore */ }
                    window.location.reload()
                  }}
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
                >
                  <RefreshCw className="h-4 w-4" />
                  Reload
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-[400px] flex items-center justify-center p-8">
      <div className="max-w-lg w-full rounded-lg border border-error/40 bg-card p-6 shadow-sm">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-error shrink-0 mt-0.5" aria-hidden />
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold">Something went wrong</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              {error?.message ?? "An unexpected error occurred while rendering this page."}
            </p>

            {isDev && error?.stack ? (
              <details className="mt-3">
                <summary className="cursor-pointer text-xs text-muted-foreground hover:text-foreground">
                  Stack trace (dev only)
                </summary>
                <pre className="mt-2 overflow-auto rounded bg-muted p-2 text-xs">
                  {error.stack}
                </pre>
              </details>
            ) : null}

            <div className="mt-5 flex gap-2">
              <button
                type="button"
                onClick={resetErrorBoundary}
                className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                <RotateCcw className="h-4 w-4" />
                Try again
              </button>
              <a
                href="/dashboard"
                className="inline-flex items-center gap-2 rounded-md border border-border bg-transparent px-3 py-1.5 text-sm font-medium text-foreground hover:bg-primary-light hover:text-primary transition-colors"
              >
                <Home className="h-4 w-4" />
                Go to dashboard
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
